import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { pharmacyApi } from "@/api/pharmacy";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Settings2, Loader2, Plus, Trash2, ChevronDown } from "lucide-react";

type Drug = { id: string; name: string; strength?: string; form?: string; unit?: string };

export default function OTPharmacyDashboardPage() {
  const qc = useQueryClient();
  const [configOpen, setConfigOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [draft, setDraft] = useState<Record<string, { minQty: string; reorderQty: string }>>({});

  const { data } = useQuery({
    queryKey: ["ot-dashboard"],
    queryFn: async () => {
      const res = await pharmacyApi.otDashboard();
      return (res.data as any)?.data;
    },
  });

  const { data: minStockList = [] } = useQuery({
    queryKey: ["ot-min-stock"],
    queryFn: async () => {
      const res = await pharmacyApi.listOTMinStock();
      return (res.data as any)?.data ?? [];
    },
  });

  const { data: drugs = [] } = useQuery({
    queryKey: ["drugs-list"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const minStockByDrugId = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of minStockList as any[]) map.set(row.drugId, row);
    return map;
  }, [minStockList]);

  const items = data?.items ?? [];
  const low = items.filter((i: any) => i.status === "LOW");

  const drugById = useMemo(() => {
    const map = new Map<string, Drug>();
    for (const d of drugs as Drug[]) map.set(d.id, d);
    return map;
  }, [drugs]);

  const selectedDrugIds = useMemo(() => {
    const set = new Set<string>();
    for (const row of minStockList as any[]) {
      if (row?.drugId) set.add(row.drugId);
    }
    for (const k of Object.keys(draft)) set.add(k);
    const ids = Array.from(set);
    ids.sort((a, b) => {
      const da = drugById.get(a);
      const db = drugById.get(b);
      const an = (da?.name ?? a).toLowerCase();
      const bn = (db?.name ?? b).toLowerCase();
      return an.localeCompare(bn);
    });
    return ids;
  }, [minStockList, draft, drugById]);

  const filteredAddOptions = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    const list = drugs as Drug[];
    const filtered = q
      ? list.filter((d) => d.name.toLowerCase().includes(q) || (d.strength ?? "").toLowerCase().includes(q))
      : list;
    return filtered.slice(0, 30);
  }, [addQuery, drugs]);

  const addDrugToDraft = (drugId: string) => {
    const existing = minStockByDrugId.get(drugId);
    setDraft((p) => ({
      ...p,
      [drugId]: {
        minQty: String(existing?.minQty ?? 0),
        reorderQty: String(existing?.reorderQty ?? 0),
      },
    }));
  };

  const removeDrugFromDraftAndView = (drugId: string) => {
    // If it was only drafted and not saved, remove from draft.
    // If it is saved, keep row visible but clear edits only (draft removed).
    setDraft((p) => {
      const next = { ...p };
      delete next[drugId];
      return next;
    });
  };

  const upsertMutation = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(draft)
        .map(([drugId, v]) => ({
          drugId,
          minQty: parseInt(v.minQty, 10),
          reorderQty: parseInt(v.reorderQty, 10),
        }))
        .filter((x) => x.drugId && Number.isFinite(x.minQty) && x.minQty >= 0 && Number.isFinite(x.reorderQty) && x.reorderQty >= 0);

      if (entries.length === 0) throw new Error("Set at least one min-stock line");

      // Sequential upserts (API is per-drug)
      for (const e of entries) {
        await pharmacyApi.upsertOTMinStock(e);
      }
    },
    onSuccess: async () => {
      toast.success("OT min-stock saved");
      setDraft({});
      setConfigOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["ot-min-stock"] }),
        qc.invalidateQueries({ queryKey: ["ot-dashboard"] }),
      ]);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed to save"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-foreground">OT Pharmacy Dashboard</CardTitle>
            <Button variant="outline" onClick={() => setConfigOpen(true)} className="text-foreground">
              <Settings2 className="h-4 w-4 mr-2" />
              Configure min stock
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Current OT stock vs minimum levels (based on min-stock config).
        </CardContent>
      </Card>

      {low.length > 0 && (
        <Card className="border-amber-300">
          <CardHeader className="flex flex-row items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-base">Low stock alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              {low.map((r: any) => (
                <li key={r.drug?.id}>
                  {r.drug?.name} {r.drug?.strength ? `(${r.drug.strength})` : ""}: {r.currentQty} / min {r.minQty}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base text-foreground">OT Stock Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm text-foreground">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left p-2 font-medium">Drug</th>
                  <th className="text-right p-2 font-medium">Current</th>
                  <th className="text-right p-2 font-medium">Min</th>
                  <th className="text-left p-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={4} className="p-3 text-muted-foreground">No min-stock configured yet.</td></tr>
                ) : (
                  items.map((r: any) => (
                    <tr key={r.drug?.id} className="border-t">
                      <td className="p-2">{r.drug?.name} {r.drug?.strength ? `(${r.drug.strength})` : ""}</td>
                      <td className="p-2 text-right">{r.currentQty}</td>
                      <td className="p-2 text-right">{r.minQty}</td>
                      <td className="p-2">
                        {r.status === "LOW" ? (
                          <span className="inline-flex items-center gap-2 text-amber-700">
                            <AlertTriangle className="h-4 w-4" /> LOW
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2 text-emerald-700">
                            <CheckCircle2 className="h-4 w-4" /> OK
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[calc(100vh-2rem)] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Configure OT min stock</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-muted-foreground">
                Add medicines and set <span className="font-medium">Min Qty</span> + <span className="font-medium">Reorder Qty</span>. These drive OT dashboard low-stock alerts.
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDraft({})}
                  disabled={upsertMutation.isPending}
                >
                  Clear edits
                </Button>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap items-center justify-between">
              <Popover open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setAddQuery(""); }}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" disabled={upsertMutation.isPending}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add medicine
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[420px] p-3">
                  <div className="space-y-2">
                    <Input
                      placeholder="Search medicine..."
                      value={addQuery}
                      onChange={(e) => setAddQuery(e.target.value)}
                      autoFocus
                    />
                    <div className="max-h-56 overflow-y-auto border rounded-md">
                      {filteredAddOptions.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground">No medicines found.</div>
                      ) : (
                        <div className="divide-y">
                          {filteredAddOptions.map((d) => {
                            const already = selectedDrugIds.includes(d.id);
                            return (
                              <button
                                key={d.id}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={already}
                                onClick={() => {
                                  addDrugToDraft(d.id);
                                  setAddOpen(false);
                                  setAddQuery("");
                                }}
                              >
                                <div className="font-medium text-sm">
                                  {d.name} {d.strength ? `(${d.strength})` : ""}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {d.form ?? ""} {d.unit ? `· ${d.unit}` : ""} {already ? "· already added" : ""}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="text-xs text-muted-foreground">
                Showing <span className="font-medium">{selectedDrugIds.length}</span> medicine(s)
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm text-foreground">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left p-2 font-medium">Drug</th>
                    <th className="text-right p-2 font-medium">Current saved min</th>
                    <th className="text-right p-2 font-medium">Current saved reorder</th>
                    <th className="text-right p-2 font-medium">Min Qty</th>
                    <th className="text-right p-2 font-medium">Reorder Qty</th>
                    <th className="p-2 w-[56px]" />
                  </tr>
                </thead>
                <tbody>
                  {selectedDrugIds.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-3 text-muted-foreground">
                        No medicines added yet. Click <span className="font-medium">Add medicine</span> to start.
                      </td>
                    </tr>
                  ) : (
                    selectedDrugIds.map((drugId) => {
                      const d = drugById.get(drugId);
                      const saved = minStockByDrugId.get(drugId);
                      const edit =
                        draft[drugId] ??
                        (saved ? { minQty: String(saved.minQty), reorderQty: String(saved.reorderQty) } : { minQty: "0", reorderQty: "0" });
                      return (
                        <tr key={drugId} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">
                              {d?.name ?? "—"} {d?.strength ? `(${d.strength})` : ""}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {d?.form ?? ""} {d?.unit ? `· ${d.unit}` : ""}
                            </div>
                          </td>
                          <td className="p-2 text-right">{saved?.minQty ?? "-"}</td>
                          <td className="p-2 text-right">{saved?.reorderQty ?? "-"}</td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={edit.minQty}
                              placeholder="0"
                              onChange={(e) =>
                                setDraft((p) => ({
                                  ...p,
                                  [drugId]: { minQty: e.target.value, reorderQty: edit.reorderQty ?? "0" },
                                }))
                              }
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={edit.reorderQty}
                              placeholder="0"
                              onChange={(e) =>
                                setDraft((p) => ({
                                  ...p,
                                  [drugId]: { minQty: edit.minQty ?? "0", reorderQty: e.target.value },
                                }))
                              }
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeDrugFromDraftAndView(drugId)}
                              title="Remove from edits"
                              disabled={upsertMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">
              Tip: Add a medicine, enter Min/Reorder values, then click Save. You can edit multiple medicines before saving.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)} disabled={upsertMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => upsertMutation.mutate()} disabled={upsertMutation.isPending}>
              {upsertMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                </span>
              ) : (
                "Save min stock"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

